import { v4 as uuidv4 } from 'uuid';

const shortAnswerResponses = [
  'Very satisfied with the overall experience.',
  'Could use some improvements in certain areas.',
  'The service exceeded my expectations.',
  'Average experience, nothing special.',
  'Really appreciated the quick response time.',
  'Would recommend to friends and family.',
  'Some issues but generally positive.',
  'Excellent quality and value.',
  'Met my basic needs adequately.',
  'Outstanding customer service.',
  'Room for improvement in delivery.',
  'Professional and efficient.',
  'Better than expected results.',
  'Fairly good but pricey.',
  'Smooth and hassle-free process.'
];

const longAnswerResponses = [
  'Overall, I found the experience to be quite positive. The staff were helpful and knowledgeable, and they took the time to address all of my concerns. While there were a few minor issues with the process, these were quickly resolved. I would definitely consider using this service again in the future and would recommend it to others who are looking for similar solutions.',
  'The quality of the product exceeded my initial expectations. I was impressed by the attention to detail and the overall craftsmanship. The delivery was prompt, and the packaging was secure. However, I did notice that the instructions could have been clearer. Despite this minor issue, I am very satisfied with my purchase and would buy from this company again.',
  'I had mixed feelings about my experience. On one hand, the core service was delivered as promised and met the basic requirements. On the other hand, the communication throughout the process could have been better. There were times when I felt left in the dark about the status of my request. With some improvements in customer communication, this could be an excellent service.',
  'This was my first time using this type of service, and I was pleasantly surprised by the results. The team was professional and responsive to my needs. They provided regular updates and were always available to answer questions. The final outcome was exactly what I was looking for. I highly recommend this service to anyone in a similar situation.',
  'While the product itself was good, the overall experience was somewhat disappointing. The shipping took longer than expected, and there was a lack of updates on the delivery status. When I reached out to customer service, the response was slow. The product quality partially makes up for these issues, but there is definitely room for improvement in the logistics department.',
  'I am extremely satisfied with the service I received. From start to finish, the process was smooth and well-organized. The team demonstrated expertise and professionalism at every step. They were proactive in addressing potential issues before they became problems. This is exactly the kind of service I was hoping for, and I will definitely be a returning customer.',
  'The experience was adequate but not exceptional. The service did what it was supposed to do, but there was nothing that particularly stood out. The pricing was fair, and the quality was acceptable. For someone looking for a basic solution without any frills, this might be a good option. However, those seeking a premium experience might want to look elsewhere.',
  'I was thoroughly impressed with the attention to customer needs throughout the entire process. The team went above and beyond to ensure satisfaction, offering personalized solutions that addressed my specific requirements. The value for money was excellent, and the quality exceeded what I expected at this price point. Outstanding experience overall.'
];

const sentimentPhrases = {
  positive: [
    'Excellent', 'Outstanding', 'Very satisfied', 'Highly recommend', 'Exceeded expectations',
    'Great experience', 'Wonderful service', 'Top quality', 'Fantastic results', 'Very pleased'
  ],
  neutral: [
    'Adequate', 'Acceptable', 'Average', 'Nothing special', 'Met basic needs',
    'Fair enough', 'Reasonable', 'Standard quality', 'As expected', 'Moderate'
  ],
  negative: [
    'Disappointed', 'Below expectations', 'Poor quality', 'Not satisfied', 'Needs improvement',
    'Frustrating experience', 'Would not recommend', 'Subpar service', 'Issues encountered', 'Lacking'
  ]
};

function getRandomElement(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function generateWeightedResponse(options, weights = null) {
  if (!weights) {
    return getRandomElement(options);
  }
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);
  let random = Math.random() * totalWeight;
  for (let i = 0; i < options.length; i++) {
    random -= weights[i];
    if (random <= 0) return options[i];
  }
  return options[options.length - 1];
}

function generateAnswerForQuestion(question) {
  const { type } = question;
  const options = question.options || question.settings?.options || [];

  switch (type) {
    case 'multipleChoice': {
      if (options.length === 0) return null;
      const weights = options.map((_, i) => Math.max(1, options.length - i));
      const selected = generateWeightedResponse(options, weights);
      return selected.id;
    }

    case 'checkboxes': {
      if (options.length === 0) return [];
      const numSelections = getRandomInt(1, Math.min(3, options.length));
      const shuffled = [...options].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, numSelections).map(o => o.id);
    }

    case 'dropdown': {
      if (options.length === 0) return null;
      return getRandomElement(options).id;
    }

    case 'linearScale': {
      const min = question.scaleMin || question.settings?.scaleMin || 1;
      const max = question.scaleMax || question.settings?.scaleMax || 5;
      const mid = Math.floor((min + max) / 2);
      const weights = [];
      for (let i = min; i <= max; i++) {
        const distFromMid = Math.abs(i - mid);
        weights.push(max - distFromMid + (i > mid ? 1 : 0));
      }
      const values = [];
      for (let i = min; i <= max; i++) values.push(i);
      return generateWeightedResponse(values, weights);
    }

    case 'likertScale': {
      const points = question.points || question.settings?.points || 5;
      const weights = [1, 2, 3, 3, 2];
      if (points !== 5) {
        return getRandomInt(1, points);
      }
      return generateWeightedResponse([1, 2, 3, 4, 5], weights);
    }

    case 'shortAnswer': {
      const sentiment = Math.random();
      let phrase;
      if (sentiment < 0.5) {
        phrase = getRandomElement(sentimentPhrases.positive);
      } else if (sentiment < 0.8) {
        phrase = getRandomElement(sentimentPhrases.neutral);
      } else {
        phrase = getRandomElement(sentimentPhrases.negative);
      }
      return `${phrase}. ${getRandomElement(shortAnswerResponses)}`;
    }

    case 'longAnswer': {
      return getRandomElement(longAnswerResponses);
    }

    case 'yesNo': {
      return Math.random() > 0.4 ? 'yes' : 'no';
    }

    case 'email': {
      const domains = ['gmail.com', 'yahoo.com', 'outlook.com', 'company.org', 'university.edu'];
      const names = ['user', 'respondent', 'participant', 'member', 'person'];
      return `${getRandomElement(names)}${getRandomInt(1, 999)}@${getRandomElement(domains)}`;
    }

    case 'number': {
      const min = question.min ?? question.settings?.min ?? 1;
      const max = question.max ?? question.settings?.max ?? 100;
      return getRandomInt(min, max);
    }

    case 'ranking': {
      if (options.length === 0) return [];
      return [...options].sort(() => Math.random() - 0.5).map(o => o.id);
    }

    default:
      return null;
  }
}

export function generateSampleResponses(survey, count = 400) {
  const responses = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const answers = {};
    
    survey.questions.forEach(question => {
      const answer = generateAnswerForQuestion(question);
      if (answer !== null && answer !== undefined) {
        answers[question.id] = answer;
      }
    });

    const daysAgo = Math.floor(Math.random() * 30);
    const hoursAgo = Math.floor(Math.random() * 24);
    const minutesAgo = Math.floor(Math.random() * 60);
    const submittedAt = new Date(now);
    submittedAt.setDate(submittedAt.getDate() - daysAgo);
    submittedAt.setHours(submittedAt.getHours() - hoursAgo);
    submittedAt.setMinutes(submittedAt.getMinutes() - minutesAgo);

    responses.push({
      responseId: uuidv4(),
      submittedAt: submittedAt.toISOString(),
      answers,
      completionTime: getRandomInt(60, 600)
    });
  }

  responses.sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));

  return responses;
}

export default generateSampleResponses;
